import { supabase } from '../../services/supabaseClient';
import { RESERVA_BASE } from './reserva';

/*
  Casas do Fábio no site (tarefa 87c272e9, spec371 no repositório da Bianca).
  O cadastro mora na conta nova dele na Bianca; o site só fala com funções do
  banco, que conferem quem edita (bianca_sites.editores) e devolvem à vitrine
  só as casas à venda. Nenhuma tabela é lida direto daqui.
*/

// O endereço da rota reserva é também a chave do site no banco (bianca_sites.slug).
// REACT_APP_CASAS_SITE só existe no build de teste (site fn-teste-estresse, outro empreendimento): o teste de
// estresse não pode aparecer na lista nem na vitrine do Fábio. O build de produção não define e usa o da reserva.
export const SITE = process.env.REACT_APP_CASAS_SITE || RESERVA_BASE.replace(/^\//, '');

export const STATUS = {
  disponivel: 'Disponível',
  reservado: 'Reservada',
  vendido: 'Vendida',
  indisponivel: 'Fora do site',
};

export async function vitrine() {
  const { data, error } = await supabase.rpc('vitrine_publica', { p_site: SITE });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function podeEditar() {
  const { data, error } = await supabase.rpc('site_pode_editar', { p_site: SITE });
  if (error) throw error;
  return data === true;
}

export async function listarCasas() {
  const { data, error } = await supabase.rpc('site_casas_listar', { p_site: SITE });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function salvarCasa(casa) {
  const { data, error } = await supabase.rpc('site_casa_salvar', { p_site: SITE, p_casa: casa });
  if (error) throw error;
  return data;
}

export async function apagarCasa(id) {
  const { error } = await supabase.rpc('site_casa_apagar', { p_site: SITE, p_id: id });
  if (error) throw error;
}

export async function retirarFoto(id) {
  const { error } = await supabase.rpc('site_casa_foto_retirar', { p_site: SITE, p_foto: id });
  if (error) throw error;
}

export async function fotoCapa(id) {
  const { error } = await supabase.rpc('site_casa_foto_capa', { p_site: SITE, p_foto: id });
  if (error) throw error;
}

export async function preencherComIA(texto) {
  const { data, error } = await supabase.functions.invoke('site-casa-ia', { body: { site: SITE, texto } });
  if (error) {
    // functions.invoke embrulha a resposta de erro; a mensagem útil vem no corpo.
    let msg = 'A IA não respondeu. Tente de novo.';
    try { msg = (await error.context.json()).error || msg; } catch { /* mantém a genérica */ }
    throw new Error(msg);
  }
  return data?.campos || {};
}

// Foto de celular chega com 4 a 8 MB; a vitrine não precisa disso. Reduz no
// próprio navegador para no máximo 1600 px e JPEG 0,85 antes de subir.
async function reduzir(arquivo) {
  let img;
  try {
    img = await createImageBitmap(arquivo);
  } catch {
    // HEIC de iPhone em navegador que não lê HEIC cai aqui; o erro cru do navegador não diz nada ao Fábio.
    throw new Error(`não deu para abrir "${arquivo.name}" (tente uma foto JPG)`);
  }
  const escala = Math.min(1, 1600 / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * escala);
  canvas.height = Math.round(img.height * escala);
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  return new Promise((ok, falha) => canvas.toBlob((b) => (b ? ok(b) : falha(new Error('Não deu para ler a foto'))), 'image/jpeg', 0.85));
}

async function sha256(blob) {
  const hash = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

// Sobe a foto no bucket e registra na casa. O caminho leva o sha: a mesma foto
// duas vezes cai no mesmo endereço e o banco recusa a repetição.
export async function adicionarFoto(casaId, arquivo) {
  const blob = await reduzir(arquivo);
  const hash = await sha256(blob);
  const caminho = `sites/${SITE}/casas/${casaId}/${hash.slice(0, 16)}.jpg`;
  const { error: erroUpload } = await supabase.storage.from('anuncios-media')
    .upload(caminho, blob, { contentType: 'image/jpeg', cacheControl: '31536000', upsert: false });
  // Já existir no bucket não é erro: é a mesma foto subindo de novo.
  if (erroUpload && !/exists|duplicate/i.test(erroUpload.message || '')) throw erroUpload;
  const { error } = await supabase.rpc('site_casa_foto_adicionar', {
    p_site: SITE, p_casa: casaId, p_path: caminho, p_sha256: hash, p_mime: 'image/jpeg',
  });
  if (error) {
    if (/duplicate|unique/i.test(error.message || '')) throw new Error('Essa foto já está na casa');
    throw error;
  }
}

export function formatarPreco(v) {
  if (v === null || v === undefined || v === '') return null;
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

// Linha de detalhes do cartão, só com o que foi preenchido.
export function detalhesDaCasa(c) {
  const d = [];
  if (c.quartos) d.push(`${c.quartos} quarto${c.quartos > 1 ? 's' : ''}`);
  if (c.suites) d.push(`${c.suites} suíte${c.suites > 1 ? 's' : ''}`);
  if (c.area_construida) d.push(`${Number(c.area_construida).toLocaleString('pt-BR')} m² construídos`);
  if (c.area_terreno) d.push(`Terreno ${Number(c.area_terreno).toLocaleString('pt-BR')} m²`);
  if (c.vagas) d.push(`${c.vagas} vaga${c.vagas > 1 ? 's' : ''}`);
  return d;
}
